import type { KeyboardEvent, ChangeEvent } from 'react';

import { parseFloatOrUndefined } from 'app/utils/numbers';

const validKeys = ['-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Enter'];
const ARROW_UP_KEY = 38;
const ARROW_DOWN_KEY = 40;

function preventKeypress(event: KeyboardEvent<HTMLInputElement>) {
  const keyIsInvalid = validKeys.indexOf(event.key) === -1;

  if (keyIsInvalid) {
    event.preventDefault();
  }
}

function isBetweenRange(
  value: number | undefined,
  minValue: number | undefined,
  maxValue: number | undefined
): boolean {
  if (value === undefined) {
    return true;
  }

  if (minValue === undefined && maxValue === undefined) {
    return true;
  }

  if (minValue === undefined && maxValue !== undefined && value <= maxValue) {
    return true;
  }

  if (maxValue === undefined && minValue !== undefined && value >= minValue) {
    return true;
  }

  if (minValue !== undefined && maxValue !== undefined && value >= minValue && value <= maxValue) {
    return true;
  }

  return false;
}

function handleChange(
  onChange: (value: string, event: ChangeEvent<HTMLInputElement> | WheelEvent) => void,
  minValue: number | undefined,
  maxValue: number | undefined,
  acceptFloat: boolean
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    if (acceptFloat || !value.match(/\./)) {
      let isNumber = value.match(/^-?\d*\.?\d*$/);

      if (minValue !== undefined && minValue >= 0) {
        isNumber = value.match(/^\d*\.?\d*$/);
      }

      const formatedValue = parseFloatOrUndefined(value);
      const isValid = isBetweenRange(formatedValue, minValue, maxValue);

      if (isNumber && isValid) {
        onChange(value, event);
      }
    }
  };
}

function incrementValue(
  value: string,
  amount: number,
  minValue?: number,
  maxValue?: number
): number {
  const currentValue = parseFloatOrUndefined(value);
  if (maxValue !== undefined && currentValue !== undefined && currentValue >= maxValue) {
    return maxValue;
  }

  if (minValue !== undefined && currentValue !== undefined && currentValue <= minValue) {
    return minValue;
  }

  if (value === '') {
    return amount;
  }
  if (currentValue === undefined) {
    return amount;
  }
  const incrementedValue = currentValue + amount;
  const roundBy = Math.abs(1 / amount);
  return Math.round(incrementedValue * roundBy) / roundBy;
}

function isIncrementable(value: string, maxValue: number | undefined) {
  if (maxValue === undefined) {
    return true;
  }
  const currentValue = parseFloatOrUndefined(value);
  if (currentValue === undefined) {
    return true;
  }
  return currentValue < maxValue;
}

function isDecrementable(value: string, minValue: number | undefined): boolean {
  if (minValue === undefined) {
    return true;
  }
  const currentValue = parseFloatOrUndefined(value);
  if (currentValue === undefined) {
    return true;
  }
  return currentValue > minValue;
}

function handleKeyDown(
  onChange: (value: string, event: KeyboardEvent<HTMLInputElement>) => void,
  incrementBy: number,
  minValue: number | undefined,
  maxValue: number | undefined
) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.keyCode) {
      case ARROW_UP_KEY: {
        event.preventDefault();
        event.stopPropagation();
        const newValue = incrementValue(
          event.currentTarget.value,
          incrementBy,
          undefined,
          maxValue
        );
        onChange(`${newValue}`, event);
        break;
      }
      case ARROW_DOWN_KEY: {
        event.preventDefault();
        event.stopPropagation();
        const newValue = incrementValue(
          event.currentTarget.value,
          -incrementBy,
          minValue,
          undefined
        );
        onChange(`${newValue}`, event);
        break;
      }
    }
  };
}

export {
  preventKeypress,
  handleChange,
  isDecrementable,
  isIncrementable,
  handleKeyDown,
  incrementValue
};
